
import React from "react";
import { AlertTriangle } from "lucide-react";

const XDRChallengesSection: React.FC = () => {
  return (
    <div className="max-w-4xl mx-auto">
      <h2 className="text-3xl font-bold mb-5" id="challenges">Challenges and Considerations</h2>
      <p className="text-lg text-gray-700 mb-6">
        While AI-based XDR offers transformative benefits, organizations should be aware of potential challenges:
      </p>
      
      <div className="mb-10 grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <div className="flex items-start mb-3">
            <AlertTriangle className="h-6 w-6 text-amber-500 mr-2" />
            <h4 className="font-bold text-lg">Implementation Complexity</h4>
          </div>
          <p className="text-gray-700 mb-4">
            Full XDR deployments require careful planning and often staged implementation. Organizations may need 
            to rework security processes to take full advantage of XDR capabilities.
          </p>
          <p className="text-gray-700">
            <strong>Mitigation:</strong> Work with vendors offering professional services for deployment, and 
            consider a phased implementation approach focusing on highest-value use cases first.
          </p>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <div className="flex items-start mb-3">
            <AlertTriangle className="h-6 w-6 text-amber-500 mr-2" />
            <h4 className="font-bold text-lg">AI Trust Issues</h4>
          </div>
          <p className="text-gray-700 mb-4">
            Security teams may be hesitant to trust AI-driven alerts and automated responses, particularly for 
            critical systems or sensitive environments.
          </p>
          <p className="text-gray-700">
            <strong>Mitigation:</strong> Look for solutions with explainable AI features that provide reasoning 
            behind alerts and recommended actions. Start with "human-in-the-loop" automation before moving to fully 
            autonomous responses.
          </p>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <div className="flex items-start mb-3">
            <AlertTriangle className="h-6 w-6 text-amber-500 mr-2" />
            <h4 className="font-bold text-lg">Data Privacy Concerns</h4>
          </div>
          <p className="text-gray-700 mb-4">
            XDR systems analyze vast amounts of data, potentially including sensitive information that may be 
            subject to privacy regulations in different jurisdictions.
          </p>
          <p className="text-gray-700">
            <strong>Mitigation:</strong> Implement data governance policies and choose XDR solutions with robust 
            privacy controls, data minimization features, and regional deployment options.
          </p>
        </div>
        
        <div className="bg-white p-6 rounded-lg shadow-sm border border-slate-200">
          <div className="flex items-start mb-3">
            <AlertTriangle className="h-6 w-6 text-amber-500 mr-2" />
            <h4 className="font-bold text-lg">Skills Requirements</h4>
          </div>
          <p className="text-gray-700 mb-4">
            Despite automation benefits, advanced XDR solutions still require skilled security personnel to 
            configure, tune, and manage them effectively.
          </p>
          <p className="text-gray-700">
            <strong>Mitigation:</strong> Invest in training for existing staff, consider managed XDR services 
            for skill gaps, and leverage vendor educational resources.
          </p>
        </div>
      </div>
    </div>
  );
};

export default XDRChallengesSection;
